import type {
  UpdateTenantRebrandThemeArgs,
  FetchTenantSignedProfileUrlArgs,
  UpdateTenantFeaturesArgs,
  UpdateTenantBrandingArgs,
  TenantBrandingResponse,
  TenantByDomainArgs,
  FetchTenantsArgs,
  CreateTenantArgs,
  TenantByIdArgs,
  EditTenantArgs,
} from './types/tenant.types';
import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';
import type {
  ApiSignedUploadUrl,
  UiSignedUploadUrl,
} from './types/files.types';
import type { ApiTenant } from '../schemas/tenants.schema';
import type {
  TenantContentHandoverState,
  TenantPremiumPlan,
  TenantReadinessItem,
} from '@mfw/common';

import { mapTenantFromApi } from './mappers/tenants.mappers';
import { destroy, patch, post, get, put } from './axios';

export async function fetchTenantByDomain({ domain }: TenantByDomainArgs) {
  // This public request ESTABLISHES the acting tenant required by the session barrier.
  // Sending it through that barrier creates a cycle: the barrier may mint `/auth/me`
  // without `X-Tenant-Id`, while this request cannot resolve the header until it leaves.
  const response = await get<BaseApiResponse<ApiTenant>>(
    `/v2/tenant?domain=${domain}`,
    { skipSessionRenewal: true },
  );
  if (!response?.data) return null;
  return mapTenantFromApi(response.data);
}

export const fetchTenantSignedProfileUrl = async ({
  mediaType,
  tenantId,
}: FetchTenantSignedProfileUrlArgs) => {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `/v2/tenants/${tenantId}/signed-upload-tenant-logo?mediaType=${mediaType}`,
  );
  return response?.data as UiSignedUploadUrl;
};

export const fetchTenantSignedCoverUrl = async ({
  mediaType,
  tenantId,
}: FetchTenantSignedProfileUrlArgs) => {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `/v2/tenants/${tenantId}/signed-upload-cover-photo?mediaType=${mediaType}`,
  );
  return response?.data as UiSignedUploadUrl;
};

/**
 * Branding-only write. Separate from `editTenantById` on purpose: that route is
 * super-admin-only because it edits tenant identity, while branding is open to
 * any platform admin for its own tenant.
 */
export async function updateTenantBranding({
  coverPhoto,
  logoName,
  tenantId,
}: UpdateTenantBrandingArgs) {
  const response = await patch<TenantBrandingResponse>(
    `/v2/tenants/${tenantId}/branding`,
    {
      ...(logoName !== undefined && { logoName }),
      ...(coverPhoto !== undefined && { coverPhoto }),
    },
  );

  return response?.data;
}

// `fetchProfileUrl` lived here until the tenant response grew `logoUrl`. Its
// only caller used it to overwrite `logoName` with a URL — the read/write shape
// confusion the backend fields were split to remove — so it cost a second
// request to reintroduce a bug. The route it called still exists.

export async function fetchTenantById({ id }: TenantByIdArgs) {
  const response = await get<BaseApiResponse<ApiTenant>>(`/v2/tenants/${id}`);
  if (!response?.data) return null;
  return mapTenantFromApi(response!.data);
}

export async function fetchTenantReadiness({ id }: TenantByIdArgs) {
  const response = await get<BaseApiResponse<TenantReadinessItem[]>>(
    `/v2/tenants/${id}/readiness`,
  );
  return response?.data ?? [];
}

export async function reseedTenant({ id }: TenantByIdArgs) {
  return await post<BaseApiResponse<TenantReadinessItem[]>>(
    `/v2/tenants/${id}/seed`,
  );
}

export async function fetchTenantPremiumPlans({ id }: TenantByIdArgs) {
  const response = await get<BaseApiResponse<TenantPremiumPlan[]>>(
    `/v2/tenants/${id}/premium-plans`,
  );
  return response?.data ?? [];
}

export async function updateTenantPremiumPlanPrice({
  id,
  planId,
  priceMinor,
}: TenantByIdArgs & { planId: string; priceMinor: number }) {
  return await put<BaseApiResponse<TenantPremiumPlan>>(
    `/v2/tenants/${id}/premium-plans/${planId}`,
    { priceMinor },
  );
}

export async function fetchTenantContentHandover({ id }: TenantByIdArgs) {
  const response = await get<BaseApiResponse<TenantContentHandoverState>>(
    `/v2/tenants/${id}/content-handover`,
  );
  return response?.data ?? null;
}

export async function startTenantContentHandover({
  authorEmail,
  id,
}: TenantByIdArgs & { authorEmail: string }) {
  return await post<BaseApiResponse<TenantContentHandoverState>>(
    `/v2/tenants/${id}/content-handover`,
    { authorEmail },
  );
}

export async function deleteTenantById({ id }: TenantByIdArgs) {
  return await destroy(`/v2/tenants/${id}`);
}

export async function fetchTenants(options?: FetchTenantsArgs) {
  const response = await get<BaseApiPaginatedResponse<ApiTenant[]>>(
    '/v2/tenants',
    {
      query: options?.filters,
    },
  );
  return {
    ...response,
    data: response?.data?.map(mapTenantFromApi),
  };
}

export async function createTenant({
  canHaveAdditionalOrganizations,
  canHaveIndividualTrainers,
  description,
  domain,
  keywords,
  name,
  shortName,
  splashScreenText,
}: CreateTenantArgs) {
  const response = await post<ApiTenant>(`/v2/tenants`, {
    canHaveAdditionalOrganizations,
    canHaveIndividualTrainers,
    description,
    domain,
    keywords,
    name,
    shortName,
    splashScreenText,
  });
  if (!response?.data) return null;
  return mapTenantFromApi(response?.data);
}

/**
 * Admin rebrand (Task 11): persists the tenant DEFAULT theme's editable var
 * subset. Body is the Record of editable var names → 6-digit hex; the tenant is
 * resolved from auth. The BE merges over the current TenantSettings.theme and
 * returns the merged plain object under `data.theme`.
 */
export async function updateTenantRebrandTheme({
  theme,
}: UpdateTenantRebrandThemeArgs) {
  const response = await put<{ theme: Record<string, string> }>(
    `/v2/tenants/rebrand-theme`,
    theme,
  );
  return response?.data?.theme;
}

/**
 * The ONE writer of a tenant's switches: the six features and the two
 * organization flags, all eight required by the strict DTO. The body is built
 * field by field so nothing else the caller holds can reach the route. The
 * target is the path, whatever tenant the operator has selected.
 */
export async function updateTenantFeatures({
  id,
  canHaveAdditionalOrganizations,
  canHaveIndividualTrainers,
  desk,
  lockers,
  marketplace,
  mfwContent,
  premium,
  sessions,
}: UpdateTenantFeaturesArgs) {
  const response = await put<ApiTenant>(`/v2/tenants/${id}/features`, {
    canHaveAdditionalOrganizations,
    canHaveIndividualTrainers,
    desk,
    lockers,
    marketplace,
    mfwContent,
    premium,
    sessions,
  });
  if (!response?.data) return null;
  return mapTenantFromApi(response.data);
}

export async function editTenantById({
  id,
  description,
  domain,
  keywords,
  logoName,
  name,
  shortName,
  splashScreenText,
  billingIdentity,
  defaultLocale,
  opsEmail,
}: EditTenantArgs) {
  const response = await patch<ApiTenant>(`/v2/tenants/${id}`, {
    billingIdentity,
    defaultLocale,
    opsEmail,
    description,
    domain,
    keywords,
    logoName,
    name,
    shortName,
    splashScreenText,
  });
  if (!response?.data) return null;
  return mapTenantFromApi(response?.data);
}
